import { tt } from '@agos/shared'

/**
 * The period the discount statistics are asked for - §9.296, `BACKEND.md` §10A.100.
 *
 * The owner: *"Kupóny a zľavy → Štatistiky… also things like… Filter based on day"*, and separately
 * that it should look like ***Analytika → Štatistiky***.
 *
 * **Written here rather than imported from `manager-reporting`.** That module owns `rangeFor` and the
 * same seven labels, and this is the second time a screen has wanted them - §9.207 duplicated a
 * shorter list for the same reason and said why: `manager-menu` does not depend on
 * `manager-reporting`, and a dependency between two feature modules for a date helper is a worse
 * trade than a small copy. **The definition that would actually matter if it drifted is the server's**,
 * and there is only one of those.
 *
 * **`all` is the important one and is not in the other list.** `/discounts/stats` answered all time
 * before it took a period, so *Celé obdobie* is what the screen showed for a year and must remain
 * reachable - and it is the default, so opening the tab shows what it always showed.
 */
export type CouponPeriod = 'today' | 'week' | 'month' | 'last_month' | 'all' | 'custom'

export const COUPON_PERIODS: readonly CouponPeriod[] = [
  'today',
  'week',
  'month',
  'last_month',
  'all',
  'custom',
]

export function couponPeriodLabel(period: CouponPeriod): string {
  if (period === 'today') return tt('Dnes')
  if (period === 'week') return tt('Tento týždeň')
  if (period === 'month') return tt('Tento mesiac')
  if (period === 'last_month') return tt('Minulý mesiac')
  if (period === 'custom') return tt('Vlastný rozsah')
  return tt('Celé obdobie')
}

export interface CouponRange {
  from: string
  to: string
}

function localDate(day: Date): string {
  const month = `${day.getMonth() + 1}`.padStart(2, '0')
  const date = `${day.getDate()}`.padStart(2, '0')
  return `${day.getFullYear()}-${month}-${date}`
}

/** Monday, because a Slovak week starts there and `getDay()` puts Sunday at 0. */
function mondayOf(today: Date): Date {
  const day = new Date(today)
  const weekday = (day.getDay() + 6) % 7
  day.setDate(day.getDate() - weekday)
  return day
}

/** `null` for *all time*, which is the absence the endpoint reads as "no bound". */
export function couponRange(
  period: CouponPeriod,
  today: Date,
  custom?: CouponRange,
): CouponRange | null {
  if (period === 'all') return null
  if (period === 'custom') return custom !== undefined && custom.from !== '' && custom.to !== '' ? custom : null
  if (period === 'week') return { from: localDate(mondayOf(today)), to: localDate(today) }
  if (period === 'month') {
    return { from: localDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: localDate(today) }
  }
  if (period === 'last_month') {
    return {
      from: localDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: localDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    }
  }
  return { from: localDate(today), to: localDate(today) }
}

/**
 * What `GET /discounts/stats` wants - §10A.100.
 *
 * **Timestamps, not dates, and the `to` covers the whole day.** The column is `created_at`, so a bare
 * `2026-09-09` as an upper bound means midnight and would drop everything sold that day. This is the
 * FB-115 trap avoided rather than repeated: that entry records six endpoints taking a *day count* from
 * now, which cannot express "June" at all; this one takes the range and means it.
 */
export function couponStatsParams(range: CouponRange | null): { from?: string; to?: string } {
  if (range === null) return {}
  return { from: `${range.from}T00:00:00Z`, to: `${range.to}T23:59:59Z` }
}
